/* eslint complexity: 0 */
function risk_score_from_inputs(row, form) {
  const raw_score = form.risk_score ?? row.risk?.label?.match(/\d+/)?.[0] ?? 42;
  const parsed_score = Number.parseInt(raw_score, 10);
  return Number.isNaN(parsed_score) ? 42 : parsed_score;
}

function title_case_words(value) {
  return value
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function document_tone_from_status(status_label) {
  if (status_label === 'Expired') return 'danger';
  if (status_label === 'Expiring') return 'warning';
  return 'success';
}

function monitoring_note(status_label) {
  const note = status_label === 'Revalidation'
    ? 'Monitoring has flagged this supplier for revalidation. OnboardOS has been reopened and the register remains read-only until a clean return.'
    : 'Complyon is monitoring this active supplier. Intake and due diligence live in OnboardOS - use Revalidate to trigger re-onboarding.';
  return note;
}

function workflow_due_label(status_label, supplier_since) {
  const due_label = status_label === 'Revalidation'
    ? 'In revalidation'
    : `Due ${supplier_since || 'Mar 2024'}`;
  return due_label;
}

function workflow_steps(status_label) {
  return [
    { label: 'Renewal notice', state: status_label === 'Onboarding' ? 'active' : 'done' },
    { label: 'Documents collected', state: status_label === 'Onboarding' ? 'pending' : 'active' },
    { label: 'Compliance reviewed', state: 'pending' },
    { label: 'Renewed', state: 'pending' }
  ];
}

function linked_items(row, form, normalise_control) {
  const control_value = normalise_control(form.linked_control || row.control);
  const base_items = [
    { icon: 'bi bi-file-earmark-text', label: 'Policy', value: form.linked_policy || row.policy || 'Policy not linked' },
    { icon: 'bi bi-people', label: 'Role / audience', value: form.audience || row.audience },
    { icon: 'bi bi-collection', label: 'Evidence vault', value: `${form.completed_people ? 1 : 0} certificates` },
    { icon: 'bi bi-tag', label: 'Category', value: form.category || row.category }
  ];
  if (control_value === 'â€”') return base_items;
  return [
    base_items[0],
    base_items[1],
    { icon: 'bi bi-shield-check', label: 'Control', value: control_value },
    base_items[2],
    base_items[3]
  ];
}

function supplier_stats(row, risk_meta, status_meta, safe_risk) {
  const documents_value = row.documents || 58;
  const performance_value = Math.max(44, Math.min(88, 100 - Math.max(safe_risk - 10, 0)));
  const stats = [
    { label: 'Risk score', value: `${safe_risk}/100`, tone: risk_meta.drawer_tone, note: title_case_words(risk_meta.drawer_tone) },
    { label: 'Documents valid', value: `${documents_value}%`, tone: documents_value >= 80 ? 'success' : documents_value >= 50 ? 'warning' : 'danger', progress: documents_value },
    { label: 'Payments', value: status_meta.payments.label, tone: status_meta.drawer_tone, note: status_meta.payment_note },
    { label: 'Performance', value: `${performance_value}/100`, tone: performance_value >= 75 ? 'success' : performance_value >= 55 ? 'warning' : 'danger', note: performance_value >= 75 ? 'Good' : performance_value >= 55 ? 'Stable' : 'Poor' }
  ];
  return stats;
}

function update_document_record(record, document_row) {
  const matches_document = record.title.toLowerCase() === document_row.document.toLowerCase()
    || !document_row.reference
    || document_row.reference === '-'
    || record.subtitle === document_row.reference;
  if (!matches_document) return record;
  return { ...record, status: document_row.status?.label || record.status, status_class: document_row.status?.class || record.status_class, age: document_row.age || record.age };
}

export function build_dynamic_drawer_impl(row, form, helpers) {
  const safe_risk = risk_score_from_inputs(row, form);
  const risk_meta = helpers.risk_tone_from_score(safe_risk);
  const status_label = form.status || row.status.label;
  const status_meta = helpers.status_meta_from_label(status_label);
  const annual_spend = form.annual_spend || 'R 1.2m / yr';
  const supplier_since = form.supplier_since || 'Mar 2024';
  const owner = form.owner || 'Procurement';

  const dynamic_drawer = {
    section: `SUPPLIER - ${(form.category || row.category || 'Technology').toUpperCase()} - SINCE ${supplier_since.toUpperCase()}`,
    title: row.supplier,
    subtitle: `${owner} - ${annual_spend}`,
    status: status_label,
    status_class: status_meta.class,
    monitoring_note: monitoring_note(status_label),
    stats: supplier_stats(row, risk_meta, status_meta, safe_risk),
    records: [
      { title: 'CSD registration', subtitle: 'MAAA-new', status: 'Pending', status_class: 'bg-light text-secondary', age: '' },
      { title: 'Tax clearance', subtitle: 'Awaiting upload', status: 'Pending', status_class: 'bg-light text-secondary', age: '' },
      { title: 'BBBEE certificate', subtitle: 'Awaiting upload', status: 'Pending', status_class: 'bg-light text-secondary', age: '' },
      { title: 'Banking confirmation', subtitle: 'Pending setup', status: '', status_class: 'bg-light text-secondary', age: '' }
    ],
    incidents: [],
    workflow: {
      due: workflow_due_label(status_label, supplier_since),
      steps: workflow_steps(status_label)
    },
    linked_to: linked_items(row, form, helpers.normalise_control),
    evidence: [{ title: 'Monitoring profile created', subtitle: 'Created just now' }],
    footer_actions: [
      { label: status_label === 'Suspended' ? 'Release payments' : 'Block payments', variant: 'outline-secondary', position: 'left', icon: status_label === 'Suspended' ? 'bi-unlock' : 'bi-pause-circle' },
      { label: 'Revalidate in OnboardOS', variant: 'primary', position: 'right', icon: 'bi-box-arrow-up-right' }
    ]
  };
  return dynamic_drawer;
}

export function build_document_drawer_impl(document_row, supplier_row, supplier_drawer) {
  const status_label = document_row.status?.label || 'Valid';
  const document_drawer = {
    ...supplier_drawer,
    section: `DOCUMENT EXPIRY - ${document_row.document.toUpperCase()} - ${status_label.toUpperCase()}`,
    title: supplier_row.supplier,
    subtitle: `${document_row.document} - ${document_row.reference}`,
    status: status_label,
    status_class: document_row.status?.class || supplier_drawer.status_class,
    monitoring_note: `${document_row.document} for ${supplier_row.supplier} is ${status_label.toLowerCase()}. Monitor expiry impact, collect refreshed evidence, and decide whether revalidation or payment controls are needed.`,
    stats: [
      { label: 'Document status', value: status_label, tone: document_tone_from_status(status_label), note: document_row.age || document_row.expires },
      { label: 'Expires', value: document_row.expires, tone: document_tone_from_status(status_label), note: document_row.reference },
      ...(supplier_drawer.stats || []).slice(0, 2)
    ],
    records: (supplier_drawer.records || []).map((record) => update_document_record(record, document_row)),
    incidents: [{ title: `${document_row.document} tracked for supplier register`, subtitle: `Reference ${document_row.reference} - ${document_row.expires}`, status: status_label, status_class: document_row.status?.class || supplier_drawer.status_class }, ...(supplier_drawer.incidents || [])],
    evidence: [{ title: `${document_row.document} record`, subtitle: `${document_row.reference} - ${document_row.expires}` }, ...(supplier_drawer.evidence || [])],
    footer_actions: [
      { label: 'Open evidence', variant: 'outline-secondary', position: 'left', icon: 'bi-folder' },
      { label: status_label === 'Expired' ? 'Revalidate in OnboardOS' : 'Schedule renewal', variant: 'primary', position: 'right', icon: status_label === 'Expired' ? 'bi-box-arrow-up-right' : 'bi-calendar-check' }
    ]
  };
  return document_drawer;
}

export function build_renewal_drawer_impl(renewal_row, supplier_row, supplier_drawer) {
  const active_step = (renewal_row.workflow || []).find((step) => step.state === 'active');
  const done_count = (renewal_row.workflow || []).filter((step) => step.state === 'done').length;
  const overdue = renewal_row.renewal_due === 'Overdue';

  function renewal_stats() {
    const stats = [
      { label: 'Renewal due', value: renewal_row.renewal_due, tone: overdue ? 'danger' : renewal_row.renewal_due.includes('2028') ? 'success' : 'warning', note: overdue ? 'Past due' : 'Upcoming' },
      { label: 'Steps complete', value: `${done_count}/${(renewal_row.workflow || []).length}`, tone: done_count === (renewal_row.workflow || []).length ? 'success' : 'warning', note: active_step ? `Active: ${active_step.label}` : 'Renewed' },
      ...(supplier_drawer.stats || []).slice(0, 2)
    ];
    return stats;
  }

  function renewal_incidents() {
    const incidents = [{ title: `Renewal workflow status - ${active_step ? active_step.label : 'Renewed'}`, subtitle: overdue ? 'Follow up required to clear the overdue renewal.' : `Next due milestone is ${renewal_row.renewal_due}.`, status: overdue ? 'Open' : 'On track', status_class: overdue ? 'bg-warning-subtle text-warning-emphasis' : 'bg-success-subtle text-success-emphasis' }, ...(supplier_drawer.incidents || [])];
    return incidents;
  }

  function renewal_evidence() {
    const evidence = [{ title: `${supplier_row.supplier} renewal pack`, subtitle: active_step ? `Current stage: ${active_step.label}` : 'Workflow complete' }, ...(supplier_drawer.evidence || [])];
    return evidence;
  }

  function renewal_footer_actions() {
    const actions = [
      { label: overdue ? 'Block payments' : 'Open evidence', variant: 'outline-secondary', position: 'left', icon: overdue ? 'bi-pause-circle' : 'bi-folder' },
      { label: 'Revalidate in OnboardOS', variant: 'primary', position: 'right', icon: 'bi-box-arrow-up-right' }
    ];
    return actions;
  }

  const renewal_drawer = {
    ...supplier_drawer,
    section: `RENEWAL WORKFLOW - ${supplier_row.category.toUpperCase()} - ${renewal_row.renewal_due.toUpperCase()}`,
    title: supplier_row.supplier,
    subtitle: `Renewal due ${renewal_row.renewal_due} - ${supplier_drawer.subtitle}`,
    status: overdue ? 'Action required' : supplier_drawer.status,
    status_class: overdue ? 'bg-danger-subtle text-danger-emphasis' : supplier_drawer.status_class,
    monitoring_note: `Supplier renewal is being tracked in Complyon. Current stage: ${active_step ? active_step.label : 'Renewed'} - use the drawer to review documents, incidents, and next actions before revalidating in OnboardOS.`,
    stats: renewal_stats(),
    incidents: renewal_incidents(),
    workflow: { due: `Due ${renewal_row.renewal_due}`, steps: renewal_row.workflow || [] },
    evidence: renewal_evidence(),
    footer_actions: renewal_footer_actions()
  };
  return renewal_drawer;
}

export function build_incident_drawer_impl(incident_row, supplier_row, supplier_drawer) {
  const severity_label = incident_row.severity?.label || 'Low';
  const severity_tone = severity_label === 'High' ? 'danger' : severity_label === 'Medium' ? 'warning' : 'success';
  const incident_drawer = {
    ...supplier_drawer,
    section: `INCIDENT & PERFORMANCE - ${incident_row.type.toUpperCase()} - ${incident_row.date.toUpperCase()}`,
    title: supplier_row.supplier,
    subtitle: `${incident_row.type} - ${incident_row.incident}`,
    status: incident_row.status?.label || supplier_drawer.status,
    status_class: incident_row.status?.class || supplier_drawer.status_class,
    monitoring_note: `${supplier_row.supplier} has an incident on record. Review the severity, supporting evidence, and supplier history before deciding whether to resolve, escalate, or trigger revalidation.`,
    stats: [
      { label: 'Incident type', value: incident_row.type, tone: severity_tone, note: incident_row.date },
      { label: 'Severity', value: severity_label, tone: severity_tone, note: incident_row.status?.label || 'Logged' },
      ...(supplier_drawer.stats || []).slice(0, 2)
    ],
    incidents: [{ title: incident_row.incident, subtitle: `${incident_row.date} - ${incident_row.type}`, status: incident_row.status?.label || 'Open', status_class: incident_row.status?.class || 'bg-warning-subtle text-warning-emphasis' }, ...(supplier_drawer.incidents || [])],
    evidence: [{ title: `${incident_row.type} incident record`, subtitle: `${incident_row.date} - ${severity_label} severity` }, ...(supplier_drawer.evidence || [])],
    footer_actions: [
      { label: 'Open evidence', variant: 'outline-secondary', position: 'left', icon: 'bi-folder' },
      { label: 'Revalidate in OnboardOS', variant: 'primary', position: 'right', icon: 'bi-box-arrow-up-right' }
    ]
  };
  return incident_drawer;
}

export function build_flagged_drawer_impl(flagged_row, supplier_row, supplier_drawer) {
  const flagged_drawer = {
    ...supplier_drawer,
    section: `FLAGGED IN REGISTER - ${supplier_row.category.toUpperCase()} - REVALIDATION`,
    title: supplier_row.supplier,
    subtitle: `Flagged for revalidation - ${supplier_drawer.subtitle}`,
    status: 'Revalidation',
    status_class: 'bg-warning-subtle text-warning-emphasis',
    monitoring_note: 'This supplier remains visible in the register while revalidation is in progress. Monitoring stays read-only until OnboardOS returns a clean activation and the supplier can move back to active oversight.',
    stats: [
      { label: 'Register state', value: 'Flagged', tone: 'warning', note: 'Read-only monitoring' },
      { label: 'Risk', value: flagged_row.risk?.label || 'Medium', tone: 'warning', note: flagged_row.payments?.label || 'Clear' },
      ...(supplier_drawer.stats || []).slice(0, 2)
    ],
    incidents: [{ title: 'Supplier flagged for revalidation in register', subtitle: 'Monitoring continues while the onboarding case is reopened in OnboardOS.', status: 'In progress', status_class: 'bg-warning-subtle text-warning-emphasis' }, ...(supplier_drawer.incidents || [])],
    workflow: { due: 'Awaiting OnboardOS return', steps: [{ label: 'Flagged in register', state: 'done' }, { label: 'OnboardOS reopened', state: 'active' }, { label: 'Compliance reviewed', state: 'pending' }, { label: 'Returned active', state: 'pending' }] },
    evidence: [{ title: 'Revalidation trigger memo', subtitle: 'Supplier sent back to OnboardOS for full re-check' }, ...(supplier_drawer.evidence || [])],
    footer_actions: [
      { label: 'Freeze changes', variant: 'outline-secondary', position: 'left', icon: 'bi-lock' },
      { label: 'Revalidate in OnboardOS', variant: 'primary', position: 'right', icon: 'bi-box-arrow-up-right' }
    ]
  };
  return flagged_drawer;
}
